import searchInsights from 'search-insights';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import {
  TaddedToCartObjectIDs,
  TaddedToCartObjectIDsAfterSearch,
  TclickedFilters,
  TclickedObjectIDs,
  TclickedObjectIDsAfterSearch,
  TconvertedFilters,
  TconvertedObjectIDs,
  TconvertedObjectIDsAfterSearch,
  TpurchasedObjectIDs,
  TviewedFilters,
  TviewedObjectIDs,
} from './algoliaTypes';
import { Log } from './errorLogger';

/** Algolia Insights objectIDs must match index records; strip trailing variant markers (-P, -C). */
const stripTrailingVariantSuffixesFromObjectId = (id: string): string => {
  if (!id || typeof id !== 'string') return id;
  return id.replace(/(-(C|P))+$/, '');
};

const normalizeObjectIDsForInsights = (objectIDs: unknown): string[] => {
  if (objectIDs == null) return [];
  if (Array.isArray(objectIDs)) {
    return objectIDs.map((id) =>
      stripTrailingVariantSuffixesFromObjectId(typeof id === 'string' ? id : id == null ? '' : String(id)),
    );
  }
  if (typeof objectIDs === 'string') {
    return [stripTrailingVariantSuffixesFromObjectId(objectIDs)];
  }
  return [];
};

export const clickedObjectIDsAfterSearch = ({
  index,
  eventName,
  queryID,
  objectIDs,
  positions,
}: TclickedObjectIDsAfterSearch) => {
  try {
    const normalizedObjectIDs = normalizeObjectIDsForInsights(objectIDs);
    searchInsights('clickedObjectIDsAfterSearch', {
      index: index,
      eventName: eventName,
      queryID: queryID,
      objectIDs: normalizedObjectIDs,
      positions: positions,
    });
  } catch (error) {
    Log.error(new Error(`The event was not triggered ${typeof error}`));
  }
};

export const convertedObjectIDsAfterSearch = ({
  index,
  eventName,
  queryID,
  objectIDs,
}: TconvertedObjectIDsAfterSearch) => {
  try {
    const normalizedObjectIDs = normalizeObjectIDsForInsights(objectIDs);
    searchInsights('convertedObjectIDsAfterSearch', {
      index: index,
      eventName: eventName,
      queryID: queryID,
      objectIDs: normalizedObjectIDs,
    });
  } catch (error) {
    Log.error(new Error(`The event was not triggered ${typeof error}`));
  }
};

export const addedToCartObjectIDsAfterSearch = ({
  eventName,
  index,
  queryID,
  objectIDs,
  objectData,
  currency,
}: TaddedToCartObjectIDsAfterSearch) => {
  try {
    const normalizedObjectIDs = normalizeObjectIDsForInsights(objectIDs);
    searchInsights('addedToCartObjectIDsAfterSearch' as any, {
      eventName: eventName,
      index: index,
      queryID: queryID,
      objectIDs: normalizedObjectIDs,
      objectData: objectData,
      currency: currency,
    });
  } catch (error) {
    Log.error(new Error(`The event was not triggered ${typeof error}`));
  }
};

export const clickedObjectIDs = ({ index, eventName, objectIDs }: TclickedObjectIDs) => {
  try {
    const normalizedObjectIDs = normalizeObjectIDsForInsights(objectIDs);
    searchInsights('clickedObjectIDs', {
      index: index,
      eventName: eventName,
      objectIDs: normalizedObjectIDs,
    });
  } catch (error) {
    Log.error(new Error(`The event was not triggered ${typeof error}`));
  }
};

export const convertedObjectIDs = ({ index, eventName, objectIDs }: TconvertedObjectIDs) => {
  try {
    const normalizedObjectIDs = normalizeObjectIDsForInsights(objectIDs);
    searchInsights('convertedObjectIDs', {
      index: index,
      eventName: eventName,
      objectIDs: normalizedObjectIDs,
    });
  } catch (error) {
    Log.error(new Error(`The event was not triggered ${typeof error}`));
  }
};

export const addedToCartObjectIDs = ({ index, eventName, objectIDs, objectData, currency }: TaddedToCartObjectIDs) => {
  try {
    const normalizedObjectIDs = normalizeObjectIDsForInsights(objectIDs);
    searchInsights('addedToCartObjectIDs' as any, {
      index: index,
      eventName: eventName,
      objectIDs: normalizedObjectIDs,
      objectData: objectData,
      currency: currency,
    });
  } catch (error) {
    Log.error(new Error(`The event was not triggered ${typeof error}`));
  }
};

export const purchasedObjectIDs = ({ index, eventName, objectIDs, objectData, currency }: TpurchasedObjectIDs) => {
  try {
    const normalizedObjectIDs = normalizeObjectIDsForInsights(objectIDs);
    searchInsights('purchasedObjectIDs' as any, {
      index: index,
      eventName: eventName,
      objectIDs: normalizedObjectIDs,
      objectData: objectData,
      currency: currency,
    });
  } catch (error) {
    Log.error(new Error(`The event was not triggered ${typeof error}`));
  }
};

export const clickedFilters = ({ index, eventName, filters }: TclickedFilters) => {
  try {
    searchInsights('clickedFilters', {
      index: index,
      eventName: eventName,
      filters: filters,
    });
  } catch (error) {
    Log.error(new Error(`The event was not triggered ${typeof error}`));
  }
};

export const convertedFilters = ({ index, eventName, filters }: TconvertedFilters) => {
  try {
    searchInsights('convertedFilters', {
      index: index,
      eventName: eventName,
      filters: filters,
    });
  } catch (error) {
    Log.error(new Error(`The event was not triggered ${typeof error}`));
  }
};

export const viewedObjectIDs = ({ index, eventName, objectIDs }: TviewedObjectIDs) => {
  try {
    const normalizedObjectIDs = normalizeObjectIDsForInsights(objectIDs);
    searchInsights('viewedObjectIDs', {
      index: index,
      eventName: eventName,
      objectIDs: normalizedObjectIDs,
    });
  } catch (error) {
    Log.error(new Error(`The event was not triggered ${typeof error}`));
  }
};

export const viewedFilters = ({ index, eventName, filters }: TviewedFilters) => {
  try {
    searchInsights('viewedFilters', {
      index: index,
      eventName: eventName,
      filters: filters,
    });
  } catch (error) {
    Log.error(new Error(`The event was not triggered ${typeof error}`));
  }
};

export const isClearanceEnabled = (projectSettings: ProjectSettings) => {
  return projectSettings?.isClearanceEnabled ?? false;
};

//code to check if we have to use onSale or clearance property
export const filterOnSaleItem = (objectItem: any, projectSettings: ProjectSettings) => {
  if (isClearanceEnabled(projectSettings)) {
    return (objectItem?.clearance || objectItem?.onPromoPrice) ?? false;
  }
  return objectItem?.onSale ?? false;
};
